import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, RotateCcw, Play } from "lucide-react";
import { loadHistory, saveHistory, saveCurrent, dateLabel } from "@/lib/instant";

const EASE = [0.22, 1, 0.36, 1];

export default function MonInstant() {
  const navigate = useNavigate();
  const [history, setHistory] = useState(loadHistory());

  const toggleFav = (id) => {
    const next = history.map((h) => (h.id === id ? { ...h, favorite: !h.favorite } : h));
    setHistory(next);
    saveHistory(next);
  };

  const updateNote = (id, note) => {
    const next = history.map((h) => (h.id === id ? { ...h, note } : h));
    setHistory(next);
    saveHistory(next);
  };

  const redo = (entry) => {
    saveCurrent({ pillar: entry.pillar, minutes: entry.minutes, steps: entry.steps });
    navigate("/session");
  };

  // Une séance stoppée n'est pas finie : ce lien la relance là où elle en était, sans nouveau débit
  const resume = (entry) => {
    saveCurrent({ pillar: entry.pillar, minutes: entry.minutes, steps: entry.steps, resumePct: entry.stoppedPct });
    navigate("/session");
  };

  return (
    <>
      <main className="rav-page" data-testid="mon-instant-page">
        <motion.img
          src="/logo-accueil.webp"
          alt="Respire Aime Vibre"
          className="rav-page-logo"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: EASE }}>
          <h1 className="rav-page-title" data-testid="mon-instant-title">Mes Instants</h1>
          <p className="rav-page-sub">Votre journal, en toute simplicité.</p>
          {history.length === 0 ? (
            <p className="rav-page-sub" data-testid="mon-instant-empty">
              Vos Instants terminés apparaîtront ici, tout en douceur.
            </p>
          ) : (
            <div className="rav-history-list">
              {history.map((entry, i) => (
                <motion.div
                  key={entry.id}
                  className="rav-history-card"
                  data-testid={`history-card-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.15 + i * 0.07, ease: EASE }}
                >
                  <div className="rav-history-head">
                    <span className="rav-history-date">{dateLabel(entry.date)}</span>
                    <button
                      type="button"
                      className={`rav-fav${entry.favorite ? " is-fav" : ""}`}
                      data-testid={`history-fav-${i}`}
                      aria-label="Ajouter aux favoris"
                      onClick={() => toggleFav(entry.id)}
                    >
                      <Heart size={17} strokeWidth={1.5} fill={entry.favorite ? "#c87968" : "none"} />
                    </button>
                  </div>
                  <p className="rav-history-line">
                    {entry.minutes} min — {entry.steps.map((s) => s.label).join(" • ")}
                  </p>
                  {entry.feelingAfter && <p className="rav-history-feeling">Après : {entry.feelingAfter}</p>}
                  {entry.stoppedPct != null && (
                    <p className="rav-history-stopped" data-testid={`history-stopped-${i}`}>
                      Séance stoppée à {entry.stoppedPct} %
                    </p>
                  )}
                  {entry.stoppedPct != null && (
                    <button type="button" className="rav-text-link rav-redo" data-testid={`history-resume-${i}`} onClick={() => resume(entry)}>
                      <Play size={13} strokeWidth={1.6} /> Reprendre ma séance
                    </button>
                  )}
                  <input
                    className="rav-history-note"
                    data-testid={`history-note-${i}`}
                    placeholder="Un mot pour vous aujourd'hui ?"
                    value={entry.note || ""}
                    onChange={(e) => updateNote(entry.id, e.target.value)}
                  />
                  <button type="button" className="rav-text-link rav-redo" data-testid={`history-redo-${i}`} onClick={() => redo(entry)}>
                    <RotateCcw size={13} strokeWidth={1.6} /> Refaire cet Instant
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </>
  );
}
